
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Send, Phone } from "lucide-react";
import "../App.css";
import { StructuredRenderer } from "../components/StructuredResponse";
import { BRAND_LOGO_DATA_URL } from "../constants/branding";
import { apiClient, getServerErrorMessage } from "../utils/apiClient";
import { requestUserLocation, isGeolocationAvailable } from "../services/geolocationService";
import ManagerContactWidget from "../components/ManagerContactWidget";
import { isFuzzyLocationQuery } from "../utils/fuzzyMatch";

function ChatbotPage() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [trackingSessionId, setTrackingSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [options, setOptions] = useState([]);
  const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
  const [isManagerWidgetOpen, setIsManagerWidgetOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle window close to track abandoned chats
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionId && messages.length > 0) {
        // Track abandoned chat (optional)
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [sessionId, messages]);

  const startChat = useCallback(async () => {
    try {
      setIsLoading(true);

      // Extract query parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const location = urlParams.get('location');
      const city = urlParams.get('city');
      const tableNumberFromQuery = urlParams.get('t');
      const pathToken = window.location.pathname
        .replace(/^\/+|\/+$/g, "")
        .trim();
      // Reuse existing tableNumber field for short-code URLs like /1X3CV7
      const tableNumber = tableNumberFromQuery || (pathToken && !location && !city ? pathToken : null);

      // Step 1: Initialize session (creates session IDs) with query parameters
      const initResponse = await apiClient.post("/api/chat/init", {
        location,
        city,
        tableNumber,
      });
      const newSessionId = initResponse.data.sessionId;
      const newTrackingSessionId = initResponse.data.trackingSessionId;
      const newSessionData = initResponse.data.sessionData;

      setSessionId(newSessionId);
      setTrackingSessionId(newTrackingSessionId);
      setSessionData(newSessionData);

      // Step 2: Fetch welcome message (after session is initialized)
      // Get user's timezone offset in minutes (e.g., IST = -330 for UTC+5:30)
      const timezoneOffset = new Date().getTimezoneOffset();
      
      const welcomeResponse = await apiClient.post("/api/chat/welcome", {
        sessionId: newSessionId,
        trackingSessionId: newTrackingSessionId,
        timezoneOffset: timezoneOffset,
      });

      // Display messages with delays
      const welcomeMessages = welcomeResponse.data.messages || [];
      let maxDelay = 0;

      if (welcomeMessages.length > 0) {
        welcomeMessages.forEach((message) => {
          const delay = message.delay || 0;
          maxDelay = Math.max(maxDelay, delay);
          setTimeout(() => {
            setMessages((prev) => [...prev, message]);
          }, delay);
        });
      }

      // Set options after all greeting messages have been displayed
      setTimeout(() => {
        setOptions(welcomeResponse.data.options);
      }, maxDelay + 100); // Add small buffer to ensure last message is rendered

      setHasSentFirstMessage(false);
    } catch (error) {
      console.error("Failed to start chat:", error?.response?.data || error);
      const fallbackContent = getServerErrorMessage(
        error,
        "I'm having trouble connecting right now. Please try again in a moment.",
      );

      setMessages([
        {
          role: "assistant",
          content: fallbackContent,
          timestamp: new Date().toISOString(),
          type: "text",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Start chat session when component mounts
  useEffect(() => {
    if (!sessionId) {
      startChat();
    }
  }, [sessionId, startChat]);

  const sendMessage = async (
    message,
    action = null,
    interactionType = "user_input",
    origin = "llm",
    locationData = null,
    buttonType = null,
  ) => {
    if (!message?.trim() && !action) return;

    if (!hasSentFirstMessage) {
      setHasSentFirstMessage(true);
    }

    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      type: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Use a longer timeout for chat requests since they involve LLM calls
      // Default timeout is 30s, but LLM calls can take 60-90s in some cases
      const response = await apiClient.post("/api/chat/structured", {
        message: message,
        sessionId: sessionId,
        trackingSessionId: trackingSessionId, // Send tracking session ID
        action: action,
        source: origin, // "backend" | "llm"
        interaction_type: interactionType, // "button_click" | "user_input"
        type: buttonType, // "food" | "drink" | "store" | "other"
        locationData: locationData, // Add location data for "near me" searches
      }, {
        timeout: 90000, // 90 seconds for LLM processing
      });

      // Update tracking session ID if it changed
      if (response.data.trackingSessionId) {
        setTrackingSessionId(response.data.trackingSessionId);
      }

      // Update session data if provided
      if (response.data.sessionData) {
        setSessionData(response.data.sessionData);
      }

      // Check if we received a structured response
      if (response.data.structuredResponse) {
        const botMessage = {
          role: "assistant",
          timestamp: new Date().toISOString(),
          type: "structured",
          structuredResponse: response.data.structuredResponse,
          // Pass through metadata and trackingSessionId so UI components
          // (StructuredRenderer -> CardsList) can drive "Show More" behavior
          metadata: response.data.metadata || {},
          trackingSessionId: response.data.trackingSessionId || trackingSessionId,
        };

        setMessages((prev) => [...prev, botMessage]);
        setOptions([]); // Structured responses handle their own suggestions
      } else if (response.data.messages && Array.isArray(response.data.messages)) {
        // Handle responses with messages array (e.g., menu requests, welcome messages)
        response.data.messages.forEach((msg) => {
          const botMessage = {
            role: msg.role || "assistant",
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString(),
            type: msg.type || "text",
          };
          setMessages((prev) => [...prev, botMessage]);
        });
        setOptions(response.data.options || []);
      } else {
        // Legacy fallback for plain text responses
        const botMessage = {
          role: "assistant",
          content: response.data.response || "No response",
          timestamp: new Date().toISOString(),
          type: "text",
        };

        setMessages((prev) => [...prev, botMessage]);
        setOptions(response.data.options || []);
      }
    } catch (error) {
      console.error("Failed to send message:", error?.response?.data || error);
      const serverMsg = getServerErrorMessage(error);
      const errorMessage = {
        role: "assistant",
        content: serverMsg,
        timestamp: new Date().toISOString(),
        type: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if message is a location-based query
  // Uses fuzzy matching to handle spelling mistakes and variations
  const isLocationQuery = (message) => {
    if (!message || typeof message !== 'string') {
      return false;
    }

    // Normalize the message: trim and convert to lowercase
    const normalizedMessage = message.trim().toLowerCase();
    
    // First, try exact matching for performance (fast path)
    const locationKeywords = [
      'near me',
      'nearby',
      'near by',
      'nearest',
      'closest',
      'around me',
      'close to me',
      'stores near',
      'store near',
      'stores near me',
      'store near me',
      'near my location',
      'in my area',
      'near here',
      'close by',
      'close around',
      'near my place',
      'near my home',
      'near my current location',
      'find stores near me',
      'stores around',
      'store around me',
      'store nearby',
      'stores nearby',
      'closest store',
      'closest stores',
      'any store near me',
      'search nearby',
      'location',
      'locations',
      'my location',
      'current location',
      'where am i',
      'find near',
      'show nearby',
      'nearby stores',
      'nearby store',
      'nearby locations',
      'nearby location',
      'stores',
      'store'
    ];
    
    // Fast path: Check exact match first
    const exactMatch = locationKeywords.some(keyword => normalizedMessage.includes(keyword));
    
    if (exactMatch) {
      const matchedKeyword = locationKeywords.find(k => normalizedMessage.includes(k));
      console.log("🔍 Location check (exact match):", { 
        message: normalizedMessage, 
        isMatch: true,
        matchedKeyword
      });
      return true;
    }
    
    // Slow path: Use fuzzy matching for spelling mistakes
    // Threshold of 0.70 means 70% similarity required (handles 1-2 character typos more leniently)
    const fuzzyMatch = isFuzzyLocationQuery(normalizedMessage, 0.70);
    
    console.log("🔍 Location check (fuzzy match):", { 
      message: normalizedMessage, 
      isMatch: fuzzyMatch,
      method: fuzzyMatch ? 'fuzzy' : 'none'
    });
    
    return fuzzyMatch;
  };

  // Handle location-based search
  const handleLocationSearch = async (message) => {
    if (!isGeolocationAvailable()) {
      // Browser doesn't support geolocation
      const errorMessage = {
        role: "assistant",
        content: "Your browser doesn't support location services. Please search by city name instead, like 'stores in Delhi'.",
        timestamp: new Date().toISOString(),
        type: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    try {
      // Show a loading message
      console.log("🔍 Requesting location...");
      
      // Request location permission
      const location = await requestUserLocation();
      console.log("✅ Location received:", location);
      
      // Send message with location data
      await sendMessage(message, null, "user_input", "llm", {
        lat: location.lat,
        long: location.long,
      });
    } catch (error) {
      console.error("❌ Location error:", error);
      
      // Location permission denied or failed
      let errorContent = "I couldn't access your location. ";
      
      if (error.code === 1) {
        errorContent += "You denied location permission. ";
      } else if (error.code === 2) {
        errorContent += "Location is unavailable. ";
      } else if (error.code === 3) {
        errorContent += "Location request timed out. ";
      }
      
      errorContent += "Please try searching by city name instead, like 'stores in Delhi'.";
      
      const errorMessage = {
        role: "assistant",
        content: errorContent,
        timestamp: new Date().toISOString(),
        type: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = inputMessage.trim();
    if (!message) return;

    // Check if this is a location-based query
    if (isLocationQuery(message)) {
      console.log("🔍 Location query detected:", message);
      handleLocationSearch(message);
    } else {
      console.log("🔍 Normal message:", message);
      sendMessage(message, null, "user_input", "llm");
    }
  };

  const handleSuggestionClick = (chip) => {
    // Check if the chip action/label is a location query
    const messageToCheck = chip.action || chip.label;
    if (isLocationQuery(messageToCheck)) {
      console.log("🔍 Location query detected in suggestion:", messageToCheck);
      handleLocationSearch(messageToCheck);
    } else {
      // Display label in user message, but send action to backend/LLM based on chip source
      const origin = chip.source || "backend";
      const buttonType = chip.type || "other";
      sendMessage(chip.label, chip.action, "button_click", origin, null, buttonType);
    }
  };

  const isMoreDetailsButton = (btn) => {
    if (!btn) return false;
    const label = (btn.label || "").toLowerCase();
    const action = (btn.action || "").toLowerCase();
    return (
      label.includes("more details") ||
      label === "more details" ||
      action.includes("tell me more about") ||
      action.includes("more details about")
    );
  };

  const formatMoreDetailsUserText = (btn) => {
    const raw = (btn?.action || "").trim();
    if (!raw) return btn?.label || "More Details";
    // Remove [ID:xxx] suffix before displaying to user (internal implementation detail)
    const cleanText = raw.replace(/\s*\[ID:\d+\]\s*$/i, '').trim();
    return cleanText.charAt(0).toUpperCase() + cleanText.slice(1);
  };

  const handleButtonClick = (button) => {
    // If it's a string (backward compatibility), just send it
    if (typeof button === "string") {
      if (isLocationQuery(button)) {
        console.log("🔍 Location query detected in button:", button);
        handleLocationSearch(button);
        return;
      }
      sendMessage(button, null, "button_click", "backend");
      return;
    }

    // Check if button action/label is a location query
    const messageToCheck = button.action || button.label;
    if (isLocationQuery(messageToCheck)) {
      console.log("🔍 Location query detected in button:", messageToCheck);
      handleLocationSearch(messageToCheck);
    } else {
      // Display a friendly text to the user; for "More Details" buttons, show the action text
      const displayContent = isMoreDetailsButton(button)
        ? formatMoreDetailsUserText(button)
        : button.label;

      // Send action to backend, but show formatted text in the chat
      const origin = button.source || "backend";
      const buttonType = button.type || "other";
      sendMessage(displayContent, button.action, "button_click", origin, null, buttonType);
    }
  };

  const handleOptionClick = (option) => {
    // Check if option is a location query
    const messageToCheck = option.action || option.text;
    if (isLocationQuery(messageToCheck)) {
      console.log("🔍 Location query detected in option:", messageToCheck);
      handleLocationSearch(messageToCheck);
    } else {
      const origin = option.source || "backend";
      const buttonType = option.type || "other";
      sendMessage(option.text, option.action, "button_click", origin, null, buttonType);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e); // Use handleSubmit to check for location queries
    }
  };

  const dismissKeyboard = () => {
    inputRef.current?.blur();
  };

  // Date formatting now handled by utils/dateFormatter with IST timezone

  // Format bot messages to handle line breaks, bullet points, and basic formatting
  const formatBotMessage = (message) => {
    if (!message?.content) return "";

    let content = message.content;

    // Convert markdown formatting to HTML
    // Bold: **text** -> <strong>text</strong>
    content = content.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

    // Italic: *text* -> <em>text</em> (but not when part of **)
    content = content.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");

    // Underline: __text__ -> <u>text</u>
    content = content.replace(/__([^_]+)__/g, "<u>$1</u>");

    // Split into lines and process each line
    const lines = content.split("\n");
    const elements = [];

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      const trimmedLine = line.trim();

      // Empty line - render as blank space
      if (trimmedLine === "") {
        elements.push(
          <div key={`line-${index}`} style={{ height: "12px" }}>
            &nbsp;
          </div>,
        );
      }
      // Bullet point - render with proper indentation
      else if (trimmedLine.startsWith("•") || trimmedLine.startsWith("-")) {
        elements.push(
          <div
            key={`line-${index}`}
            style={{
              marginLeft: "16px",
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: trimmedLine }}
          />,
        );
      }
      // Numbered list - render with indentation
      else if (/^\d+\./.test(trimmedLine)) {
        elements.push(
          <div
            key={`line-${index}`}
            style={{
              marginLeft: "16px",
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: trimmedLine }}
          />,
        );
      }
      // Regular line - render with normal spacing
      else {
        elements.push(
          <div
            key={`line-${index}`}
            style={{
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: line }}
          />,
        );
      }
    }

    // Add inline options if present in message
    if (message.options && message.options.length > 0) {
      elements.push(
        <div key="options" className="message-options" style={{ marginTop: "12px" }}>
          {message.options.map((option, index) => (
            <button
              key={`option-${index}`}
              onClick={() => sendMessage(option.text, option.action, "button_click")}
              disabled={isLoading}
              type="button"
              className="message-option-button"
            >
              {option.text}
            </button>
          ))}
        </div>,
      );
    }

    return elements;
  };

  // Helper function to extract suggestions from a structured response
  const extractSuggestions = (structuredResponse) => {
    if (!structuredResponse?.blocks || !Array.isArray(structuredResponse.blocks)) {
      return null;
    }

    // Find the suggestions block (should be at the end)
    const suggestionsBlock = structuredResponse.blocks.find(
      (block) => block.type === "suggestions",
    );
    return suggestionsBlock?.chips || null;
  };

  return (
    <div className="chatbot-page">
      {/* Manager Contact Widget */}
      <ManagerContactWidget
        isOpen={isManagerWidgetOpen}
        onClose={() => setIsManagerWidgetOpen(false)}
        location={sessionData?.location}
        city={sessionData?.city}
      />

      {/* Full-screen Chat Interface */}
      <div className="chat-widget chat-fullscreen">
        {/* Header */}
        <div className="chat-header">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <img src={BRAND_LOGO_DATA_URL} alt="BeanBot logo" width={40} height={40} />
              <span className="font-inter font-semibold text-white tracking-tight">BeanBot</span>
            </div>
            <div className="flex items-center space-x-3">
              {/* Manager Contact Button - Show only if location and city are available */}
              {sessionData && sessionData.location && sessionData.city && (
                <button
                  onClick={() => setIsManagerWidgetOpen(true)}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors"
                  title="Contact Manager"
                >
                  <Phone size={20} />
                </button>
              )}
              {/* Session Info */}
              {sessionData && (sessionData.location || sessionData.city || sessionData.tableNumber) && (
                <div className="flex flex-col items-end text-xs text-white/80">
                  {sessionData.location && sessionData.city && (
                    <div className="capitalize">{sessionData.location}, {sessionData.city}</div>
                  )}
                  {sessionData.tableNumber && (
                    <div>Table #{sessionData.tableNumber}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="chat-body">
          <div className="chat-messages" onClick={dismissKeyboard} onTouchStart={dismissKeyboard}>
            {messages.map((message, index) => {
              const suggestions =
                message.type === "structured"
                  ? extractSuggestions(message.structuredResponse)
                  : null;
              const messageKey = `msg-${index}-${message.timestamp || index}`;

              return (
                <React.Fragment key={messageKey}>
                  <div className={`message ${message.role === "user" ? "user" : "bot"} fade-in`}>
                    <div className="message-content">
                      <div className="message-body">
                        {message.type === "structured" ? (
                          <StructuredRenderer
                            structuredResponse={message.structuredResponse}
                            onButtonClick={(button) => handleButtonClick(button)}
                            onSuggestionClick={(chip) => handleSuggestionClick(chip)}
                            disabled={isLoading}
                            excludeSuggestions={true}
                            sessionData={sessionData}
                            // Provide metadata and trackingSessionId so CardsList
                            // can determine when to show the "Show More" button
                            metadata={message.metadata}
                            trackingSessionId={message.trackingSessionId || trackingSessionId}
                          />
                        ) : (
                          <div className="rich-text-message">
                            {message.role === "assistant"
                              ? formatBotMessage(message)
                              : message.content}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Render suggestions outside the message bubble */}
                  {suggestions && suggestions.length > 0 && (
                    <div className="inline-suggestions">
                      <div className="inline-suggestions__chips">
                        {suggestions.map((chip, chipIndex) => (
                          <button
                            key={chipIndex}
                            onClick={() => handleSuggestionClick(chip)}
                            disabled={isLoading}
                            className="suggestion-chip"
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {options.length > 0 && (
              <div className="inline-suggestions">
                <div className="inline-suggestions__chips">
                  {options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionClick(option)}
                      disabled={isLoading}
                      className="suggestion-chip"
                    >
                      {option.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoading && (
              <div className="message bot fade-in">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input - Outside chat-body to prevent it from being hidden */}
        <div className="chat-input-wrapper">
          <form onSubmit={handleSubmit} className="chat-input">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1 px-3 py-2 border border-coffee-light rounded-lg focus:outline-none focus:ring-2 focus:ring-coffee-light focus:ring-opacity-50 font-inter"
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage?.trim()}
              className="ml-2 p-2 bg-coffee-brown text-white rounded-lg hover:bg-coffee-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChatbotPage;
